// src/features/email/utils/emailHelpers.tsx

import ArchiveIcon from "@mui/icons-material/Archive";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import React from "react"; // <-- IMPORTANT: Import React for JSX

// Get file type icon with colors based on file extension
// The return type is React.ReactElement, which is what JSX expressions evaluate to.
export const getFileIcon = (filename: string): React.ReactElement => {
  const ext = filename.toLowerCase().split(".").pop() || "";
  const iconProps = { sx: { fontSize: 20 } };

  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext)) {
    return (
      <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: "#4CAF50" }} />
    );
  }
  if (["pdf"].includes(ext)) {
    return (
      <PictureAsPdfIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#F44336" }}
      />
    );
  }
  if (["doc", "docx"].includes(ext)) {
    return (
      <DescriptionIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#2196F3" }}
      />
    );
  }
  if (["txt", "rtf"].includes(ext)) {
    return (
      <DescriptionIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#607D8B" }}
      />
    );
  }
  if (["xls", "xlsx"].includes(ext)) {
    return (
      <TableViewIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#4CAF50" }}
      />
    );
  }
  if (["csv"].includes(ext)) {
    return (
      <TableViewIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#8BC34A" }}
      />
    );
  }
  if (["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(ext)) {
    return (
      <VideoFileIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#E91E63" }}
      />
    );
  }
  if (["mp3", "wav", "flac", "aac", "ogg"].includes(ext)) {
    return (
      <AudioFileIcon
        {...iconProps}
        sx={{ ...iconProps.sx, color: "#FF9800" }}
      />
    );
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
    return (
      <ArchiveIcon {...iconProps} sx={{ ...iconProps.sx, color: "#795548" }} />
    );
  }
  return (
    <InsertDriveFileIcon
      {...iconProps}
      sx={{ ...iconProps.sx, color: "#9E9E9E" }}
    />
  );
};

// Get file type category for previewing
export const getFileType = (filename: string): "image" | "pdf" | "text" | "other" => {
  const ext = filename.toLowerCase().split(".").pop() || "";
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext))
    return "image";
  if (["pdf"].includes(ext)) return "pdf";
  if (["txt"].includes(ext)) return "text";
  return "other";
};

// Format file size into a human-readable string
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Sanitize HTML content to prevent XSS attacks
export const sanitizeEmailContent = (htmlContent: string, cidMap: Record<string, string> = {}): string =>
  htmlContent
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "")
    .replace(/<img([^>]*)src=["']cid:([^"']*)["']([^>]*\/?>)/gi, (match, before, cid, after) => {
      const cleanCid = cid.replace(/[<>]/g, "");
      const resolvedSrc = cidMap[cleanCid] || cidMap[cid];
      const newSrc = resolvedSrc || "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
      return `<img${before}src="${newSrc}"${after}`;
    })
    .replace(/<img[^>]+src=["']https?:\/\/outlook\.office\.com\/mail\/id\/[^"']*["'][^>]*\/?>/gi, "");

export const extractTextFromHtml = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export const isHtmlContent = (body: string): boolean => {
  if (!body?.trim()) return false;
  return /<\/?[a-z][^>]*>/i.test(body);
};

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export function normalizeEmailBody(body: string): string {
  if (!body?.trim()) return "";

  if (isHtmlContent(body)) {
    return sanitizeEmailContent(body);
  }

  const escaped = escapeHtml(body);

  return sanitizeEmailContent(
    escaped
      .split(/\r?\n\s*\r?\n/)
      .filter((p) => p.trim())
      .map((paragraph) => `<p>${paragraph.replace(/\r?\n/g, "<br>")}</p>`)
      .join("")
  );
}

